'use client';

import {
  getProformModelSources,
  type ProformModelDescriptor,
} from '@eliadmin/core/src/components/configurator/proform-preview-assets';
import { useEffect, useState } from 'react';

export type ProformPreviewAvailability = 'available' | 'checking' | 'unavailable';

const AVAILABLE_CACHE_MS = 5 * 60 * 1000;
const UNAVAILABLE_CACHE_MS = 15 * 1000;
const availabilityCache = new Map<string, { available: boolean; expiresAt: number }>();
const pendingChecks = new Map<string, Promise<boolean>>();

function getRemoteModelUrl(model: ProformModelDescriptor | null) {
  return (
    (model &&
      getProformModelSources(model).find(source =>
        source.url.startsWith('/api/proform-preview-model?'),
      )?.url) ||
    null
  );
}

function getCachedAvailability(url: string) {
  const cached = availabilityCache.get(url);
  if (!cached) return undefined;
  if (cached.expiresAt > Date.now()) return cached.available;
  return undefined;
}

function checkAvailability(url: string) {
  const cached = getCachedAvailability(url);
  if (cached !== undefined) return Promise.resolve(cached);

  const pending = pendingChecks.get(url);
  if (pending) return pending;

  const request = fetch(url, { cache: 'no-store', method: 'HEAD' })
    .then(response => response.ok)
    .catch(() => false)
    .then(available => {
      availabilityCache.set(url, {
        available,
        expiresAt: Date.now() + (available ? AVAILABLE_CACHE_MS : UNAVAILABLE_CACHE_MS),
      });
      return available;
    })
    .finally(() => pendingChecks.delete(url));
  pendingChecks.set(url, request);
  return request;
}

export function useProformPreviewAvailability(model: ProformModelDescriptor | null) {
  const hasLocalFallback = Boolean(model?.sources.length);
  const remoteModelUrl = getRemoteModelUrl(model);
  const cachedAvailability = remoteModelUrl ? getCachedAvailability(remoteModelUrl) : undefined;
  const [result, setResult] = useState<{
    state: ProformPreviewAvailability;
    url: string;
  } | null>(null);
  const [retryRevision, setRetryRevision] = useState(0);

  useEffect(() => {
    if (!model || hasLocalFallback || !remoteModelUrl) return;

    let active = true;
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;
    setResult({ state: 'checking', url: remoteModelUrl });
    void checkAvailability(remoteModelUrl).then(available => {
      if (active) {
        setResult({ state: available ? 'available' : 'unavailable', url: remoteModelUrl });
        if (!available) {
          retryTimeout = setTimeout(() => {
            availabilityCache.delete(remoteModelUrl);
            setResult({ state: 'checking', url: remoteModelUrl });
            setRetryRevision(revision => revision + 1);
          }, UNAVAILABLE_CACHE_MS);
        }
      }
    });

    return () => {
      active = false;
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [hasLocalFallback, model, remoteModelUrl, retryRevision]);

  if (!model) return 'unavailable';
  if (hasLocalFallback) return 'available';
  if (!remoteModelUrl) return 'unavailable';
  if (cachedAvailability !== undefined) {
    return cachedAvailability ? 'available' : 'unavailable';
  }
  return result?.url === remoteModelUrl ? result.state : 'checking';
}
